import React, { useRef } from 'react';
import Slider from 'react-slick';
import type { Settings } from 'react-slick';
import { ChevronLeftRounded } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import type { CashFundCoverImage } from './cashFundDefaultImages.types';
import { CarouselWrapper, Thumb, ArrowButton } from './DefaultImageCarousel.styles';

export type DefaultImageCarouselProps = Readonly<{
  images: ReadonlyArray<CashFundCoverImage>;
  selectedUrl?: string;
  onSelect: (url: string) => void;
}>;

const DESKTOP_SLIDES = 4;
const MOBILE_SLIDES = 3;
const MOBILE_BREAKPOINT = 640;

export const DefaultImageCarousel: React.FC<DefaultImageCarouselProps> = ({ images, selectedUrl, onSelect }) => {
  const { tq } = useTranslation('cashFundsShared');
  const sliderRef = useRef<Slider>(null);

  // A single-image (or empty) set has nothing to browse.
  if (images.length <= 1) {
    return null;
  }

  const desktopSlides = Math.min(DESKTOP_SLIDES, images.length);
  const mobileSlides = Math.min(MOBILE_SLIDES, images.length);

  const settings: Settings = {
    dots: false,
    arrows: false,
    // Only fetch thumbnails as they scroll into view, not the full stock set on open.
    lazyLoad: 'ondemand',
    // Looping is only possible when at least one slide sits off-screen.
    infinite: images.length > desktopSlides,
    speed: 300,
    slidesToShow: desktopSlides,
    slidesToScroll: 1,
    swipeToSlide: true,
    responsive: [
      {
        breakpoint: MOBILE_BREAKPOINT,
        settings: { slidesToShow: mobileSlides, infinite: images.length > mobileSlides },
      },
    ],
  };

  return (
    <CarouselWrapper>
      <ArrowButton
        $side="left"
        type="button"
        aria-label={tq`Previous cover options`}
        onClick={() => sliderRef.current?.slickPrev()}
      >
        <ChevronLeftRounded size="sm" />
      </ArrowButton>

      <Slider
        ref={sliderRef}
        {...settings}
      >
        {images.map((image, index) => {
          const isSelected = image.url === selectedUrl;
          return (
            <div key={`${image.url}-${index}`}>
              <Thumb
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                aria-label={tq`Choose cover image ${index + 1}`}
                onClick={() => onSelect(image.url)}
              >
                <img
                  src={image.url}
                  alt=""
                />
              </Thumb>
            </div>
          );
        })}
      </Slider>

      <ArrowButton
        $side="right"
        type="button"
        aria-label={tq`Next cover options`}
        onClick={() => sliderRef.current?.slickNext()}
      >
        <ChevronLeftRounded size="sm" />
      </ArrowButton>
    </CarouselWrapper>
  );
};

DefaultImageCarousel.displayName = 'DefaultImageCarousel';
